import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import { Bold, Image as ImageIcon, Italic, Link2, X } from 'lucide-react';
import type { Mailbox } from '@mailpilot/shared';
import { Button, Checkbox, Field, Input, Segmented, Select, Textarea } from '../ui';
import { SubSection, TipIconButton, Well } from './SettingsPrimitives';
import { HtmlFrame } from '../ui/HtmlFrame';

export interface SignatureDraft {
  id: string | null;
  name: string;
  mailboxId: string | null;
  text: string;
  html: string;
  isDefaultNew: boolean;
  isDefaultReply: boolean;
}

export const emptySignatureDraft = (): SignatureDraft => ({
  id: null,
  name: '',
  mailboxId: null,
  text: '',
  html: '',
  isDefaultNew: false,
  isDefaultReply: false,
});

function escAttr(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escText(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function validLinkUrl(v: string): boolean {
  if (/^mailto:[^\s@]+@[^\s@]+$/i.test(v) || /^tel:[+\d\s()-]+$/i.test(v)) return true;
  try {
    const u = new URL(v);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}
function validImageUrl(v: string): boolean {
  try {
    return new URL(v).protocol === 'https:';
  } catch {
    return false;
  }
}

type Popover = null | 'link' | 'image';

function InlineInsertForm({ title, onCancel, onSubmit, children, submitLabel }: { title: string; onCancel: () => void; onSubmit: () => void; children: ReactNode; submitLabel: string }) {
  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4 rounded-2xl bg-raised p-4"
    >
      <div className="flex items-center justify-between">
        <p className="text-[0.8125rem] font-semibold text-ink">{title}</p>
        <TipIconButton size="sm" label="Cancel" tipPlacement="bottom-end" icon={<X className="h-4 w-4" aria-hidden />} onClick={onCancel} />
      </div>
      {children}
      <div className="flex justify-end gap-2">
        <Button size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" type="submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export function SignatureEditor({ draft, onChange, mailboxes }: { draft: SignatureDraft; onChange: (d: SignatureDraft) => void; mailboxes: Mailbox[] }) {
  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const sel = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const [popover, setPopover] = useState<Popover>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [imgAlt, setImgAlt] = useState('');
  const [imgWidth, setImgWidth] = useState('160');
  const [formError, setFormError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');

  const set = <K extends keyof SignatureDraft>(k: K, v: SignatureDraft[K]) => onChange({ ...draft, [k]: v });

  const rememberSelection = () => {
    const el = htmlRef.current;
    if (el) sel.current = { start: el.selectionStart, end: el.selectionEnd };
  };

  const replaceSelection = (make: (selected: string) => string) => {
    const el = htmlRef.current;
    const { start, end } = el && document.activeElement === el ? { start: el.selectionStart, end: el.selectionEnd } : sel.current;
    const value = draft.html;
    const selected = value.slice(start, end);
    const insert = make(selected);
    const next = value.slice(0, start) + insert + value.slice(end);
    set('html', next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + insert.length;
      el.setSelectionRange(pos, pos);
      sel.current = { start: pos, end: pos };
    });
  };

  const wrap = (tag: 'strong' | 'em') => replaceSelection((s) => `<${tag}>${s || (tag === 'strong' ? 'bold text' : 'italic text')}</${tag}>`);

  const openLink = () => {
    rememberSelection();
    const { start, end } = sel.current;
    setLinkText(draft.html.slice(start, end).replace(/<[^>]*>/g, ''));
    setLinkUrl('https://');
    setFormError(null);
    setPopover('link');
  };
  const openImage = () => {
    rememberSelection();
    setImgUrl('https://');
    setImgAlt('');
    setFormError(null);
    setPopover('image');
  };

  const insertLink = () => {
    const url = linkUrl.trim();
    if (!validLinkUrl(url)) {
      setFormError('Enter a full web address (https://…), mailto: or tel: link.');
      return;
    }
    const text = linkText.trim() || url.replace(/^mailto:|^tel:/i, '');
    replaceSelection(() => `<a href="${escAttr(url)}">${escText(text)}</a>`);
    setPopover(null);
  };
  const insertImage = () => {
    const url = imgUrl.trim();
    if (!validImageUrl(url)) {
      setFormError('Images must use a secure https:// address.');
      return;
    }
    const w = Math.max(16, Math.min(600, Math.round(Number(imgWidth)) || 160));
    replaceSelection(() => `<img src="${escAttr(url)}" alt="${escAttr(imgAlt.trim() || 'Logo')}" width="${w}" style="max-width:100%;height:auto;" />`);
    setPopover(null);
  };

  const hasHtml = draft.html.trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name" required hint="Only you see this.">
          {(id) => <Input id={id} value={draft.name} placeholder="e.g. Work — full" onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <Field label="Account" hint="Which mailbox may use this signature.">
          {(id) => (
            <Select id={id} value={draft.mailboxId ?? ''} onChange={(e) => set('mailboxId', e.target.value || null)}>
              <option value="">Any account</option>
              {mailboxes.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.email})
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <Field label="Plain text version" hint="Used for plain-text emails and as a fallback.">
        {(id) => <Textarea id={id} rows={4} value={draft.text} placeholder={'Jane Doe\nAcme Ltd · +44 20 1234 5678'} onChange={(e) => set('text', e.target.value)} />}
      </Field>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="sig-html" className="text-[0.8125rem] font-medium text-ink">
          HTML version <span className="font-normal text-muted">(optional)</span>
        </label>
        <div role="toolbar" aria-label="Formatting" className="flex flex-wrap items-center gap-0.5 self-start rounded-xl bg-sunken p-1">
          <TipIconButton size="sm" label="Bold" icon={<Bold className="h-4 w-4" aria-hidden />} onMouseDown={(e) => e.preventDefault()} onClick={() => wrap('strong')} className="hover:!bg-surface" />
          <TipIconButton size="sm" label="Italic" icon={<Italic className="h-4 w-4" aria-hidden />} onMouseDown={(e) => e.preventDefault()} onClick={() => wrap('em')} className="hover:!bg-surface" />
          <span className="mx-1 h-5 w-px bg-line" aria-hidden />
          <TipIconButton size="sm" label="Insert link" active={popover === 'link'} icon={<Link2 className="h-4 w-4" aria-hidden />} onMouseDown={(e) => e.preventDefault()} onClick={openLink} className="hover:!bg-surface" />
          <TipIconButton size="sm" label="Insert image or logo" active={popover === 'image'} icon={<ImageIcon className="h-4 w-4" aria-hidden />} onMouseDown={(e) => e.preventDefault()} onClick={openImage} className="hover:!bg-surface" />
        </div>
        {popover === 'link' && (
          <InlineInsertForm title="Insert link" submitLabel="Insert link" onCancel={() => setPopover(null)} onSubmit={insertLink}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Address" error={formError ?? undefined}>
                {(id) => <Input id={id} autoFocus value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://example.com" />}
              </Field>
              <Field label="Link text">
                {(id) => <Input id={id} value={linkText} onChange={(e) => setLinkText(e.target.value)} placeholder="Our website" />}
              </Field>
            </div>
          </InlineInsertForm>
        )}
        {popover === 'image' && (
          <InlineInsertForm title="Insert image or logo" submitLabel="Insert image" onCancel={() => setPopover(null)} onSubmit={insertImage}>
            <Field label="Image address (https only)" error={formError ?? undefined} hint="Link to a hosted image. Recipients' mail apps may block remote images.">
              {(id) => <Input id={id} autoFocus value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="https://example.com/logo.png" />}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Description (alt text)">
                {(id) => <Input id={id} value={imgAlt} onChange={(e) => setImgAlt(e.target.value)} placeholder="Acme logo" />}
              </Field>
              <Field label="Width (px)">
                {(id) => <Input id={id} type="number" min={16} max={600} value={imgWidth} onChange={(e) => setImgWidth(e.target.value)} />}
              </Field>
            </div>
          </InlineInsertForm>
        )}
        <Textarea
          id="sig-html"
          ref={htmlRef}
          rows={6}
          spellCheck={false}
          className="font-mono text-[0.8125rem]"
          value={draft.html}
          placeholder={'<strong>Jane Doe</strong><br>Acme Ltd · <a href="https://acme.example">acme.example</a>'}
          onChange={(e) => set('html', e.target.value)}
          onSelect={rememberSelection}
          onBlur={rememberSelection}
        />
        <p className="text-xs text-muted">Select text and use the toolbar to format it. Scripts, forms and unsafe attributes are removed by the server when you save.</p>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[0.8125rem] font-medium text-ink">Preview</p>
          <Segmented<'html' | 'text'>
            size="sm"
            label="Preview format"
            value={previewMode}
            onChange={setPreviewMode}
            options={[
              { value: 'html', label: 'Formatted' },
              { value: 'text', label: 'Plain text' },
            ]}
          />
        </div>
        {previewMode === 'html' && hasHtml ? (
          <HtmlFrame html={draft.html} title="Signature preview" minHeight={80} maxHeight={360} />
        ) : (
          <Well>
            <pre className={clsx('min-h-[48px] whitespace-pre-wrap break-words font-sans text-[0.875rem] leading-relaxed text-ink')}>
              {draft.text || <span className="text-muted">{previewMode === 'html' ? 'No HTML version — the plain text version will be used.' : 'Nothing yet.'}</span>}
            </pre>
          </Well>
        )}
        {previewMode === 'html' && !hasHtml && draft.text && <p className="text-xs text-muted">No HTML version — showing the plain text that will be used.</p>}
      </div>

      <div className="border-t border-line/70 pt-6">
        <SubSection title="Use as default" hint="MailPilot adds a default signature exactly once per message and never duplicates it in replies or AI drafts.">
          <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            <Checkbox
              checked={draft.isDefaultNew}
              onChange={(v) => set('isDefaultNew', v)}
              label="Default for new emails"
              description={draft.mailboxId ? 'Added automatically when composing from this account.' : 'Used for any account that has no account-specific default.'}
            />
            <Checkbox checked={draft.isDefaultReply} onChange={(v) => set('isDefaultReply', v)} label="Default for replies" description="Added automatically to replies and forwards." />
          </div>
        </SubSection>
      </div>
    </div>
  );
}
